import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { bulkTagMessage } from '../lib/bulkTagMessage';
import {
    Building2, Plus, Search, Loader2, Trash2, UserPlus, X, AlertTriangle, RotateCcw, Tag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import TagInput from '../components/TagInput';
import DataTable from '../components/table/DataTable';
import { TablePagination, TableRangeBar } from '../components/table/SortableHeader';
import ViewSelector from '../components/table/ViewSelector';
import ColumnPicker from '../components/table/ColumnPicker';
import { COMPANY_FIELD_REGISTRY, DEFAULT_COMPANY_COLUMNS, buildVisibleColumns, getAllFields, isSortableColumn } from '../components/table/columnRegistry';
import useTableView from '../hooks/useTableView';
import useServerPaging, { PAGE_SIZE_OPTIONS } from '../hooks/useServerPaging';
import useCustomFields from '../hooks/useCustomFields';
import CompanyModal from '../components/companies/CompanyModal';
import { INDUSTRIES, STATUSES } from '../components/companies/companyConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// #1000: the page size is remembered per list; the Deleted tab shares it.
const PAGE_SIZE_KEY = 'nurture.list.companies.pageSize';
const SEARCH_DEBOUNCE_MS = 300;

export default function Accounts() {
    const { getAuthHeader, user } = useAuth();
    const navigate = useNavigate();

    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    // #1000: the input updates at once; the list reloads after a pause in typing.
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);
    const paging = useServerPaging(PAGE_SIZE_KEY);
    const deletedPaging = useServerPaging(PAGE_SIZE_KEY);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterIndustry, setFilterIndustry] = useState('');
    const [filterOwner, setFilterOwner] = useState(user?.user_id || ''); // Default to current user
    // Create-only on this page: rows navigate to the company detail page, which
    // owns the edit flow (same CompanyModal, `account` prop set).
    const [showModal, setShowModal] = useState(false);
    const [total, setTotal] = useState(0);

    // Team assignment
    const [showAssign, setShowAssign] = useState(false);
    const [assignAccount, setAssignAccount] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState('');

    // Selection & bulk delete
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [showBulkTagModal, setShowBulkTagModal] = useState(false);
    const [bulkTagAction, setBulkTagAction] = useState('add');
    const [bulkTags, setBulkTags] = useState([]);
    const [bulkTagging, setBulkTagging] = useState(false);

    // Deleted tab
    const [activeTab, setActiveTab] = useState('companies');
    const [deletedCompanies, setDeletedCompanies] = useState([]);
    const [deletedTotal, setDeletedTotal] = useState(0);
    const [restoring, setRestoring] = useState(false);

    // Table view management
    const {
        views, activeViewId, visibleColumnKeys, columnSizing, sorting, isDirty,
        switchView, saveView, saveAsView, deleteView, pushToOrg, addColumn,
        removeColumn, setSorting, setColumnSizing,
    } = useTableView('company', DEFAULT_COMPANY_COLUMNS);

    // Custom fields
    const { fields: customFields } = useCustomFields('account');

    // Build visible column configs
    const visibleColumns = buildVisibleColumns(COMPANY_FIELD_REGISTRY,
        visibleColumnKeys.map((key, idx) => ({ field_key: key, visible: true, order: idx, width: columnSizing[key] })),
        customFields
    );
    const allFields = getAllFields(COMPANY_FIELD_REGISTRY, customFields);
    const visibleKeySet = new Set(visibleColumnKeys);

    // #1000: the server sorts every company. A saved view sorted by a column it
    // cannot sort (the computed ones, Team) is not sent, and no header claims it.
    const sortId = sorting[0] && isSortableColumn(sorting[0].id, COMPANY_FIELD_REGISTRY) ? sorting[0].id : null;
    const sortDesc = Boolean(sortId && sorting[0].desc);
    const serverSorting = useMemo(() => (sortId ? [{ id: sortId, desc: sortDesc }] : []), [sortId, sortDesc]);

    // Load one page of companies (#1000: the server pages and sorts)
    const { page, pageSize, clampAfterLoad, resetPage } = paging;
    const latestRequest = useRef(0);
    const loadCompanies = useCallback(async (retryCount = 0) => {
        const request = ++latestRequest.current;
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            if (filterStatus) params.set('status', filterStatus);
            if (filterIndustry) params.set('industry', filterIndustry);
            if (filterOwner) params.set('owner_id', filterOwner);
            if (sortId) {
                params.set('sort_by', sortId);
                params.set('sort_direction', sortDesc ? 'desc' : 'asc');
            }
            params.set('page', String(page));
            params.set('page_size', String(pageSize));
            const res = await axios.get(`${API}/accounts?${params}`, { headers: getAuthHeader() });
            if (request !== latestRequest.current) return; // a newer page or filter won
            const rows = Array.isArray(res.data) ? res.data : (res.data.data || []);
            const count = Array.isArray(res.data) ? res.data.length : (res.data.total || 0);
            setCompanies(rows);
            setTotal(count);
            clampAfterLoad(rows.length, count);
        } catch (err) {
            if (request !== latestRequest.current) return; // a newer page or filter won; never retry the old one
            console.error('Load companies error:', err?.response?.status, err?.message);
            // Auto-retry once on network error (CORS/abort during navigation)
            if (retryCount < 1 && (!err?.response || err?.message === 'Network Error')) {
                setTimeout(() => loadCompanies(retryCount + 1), 1000);
                return;
            }
            if (err?.response?.status === 401) {
                toast.error('Session expired — please refresh the page');
            }
            // Don't show error toast for network issues — will retry
        } finally { setLoading(false); }
    }, [search, filterStatus, filterIndustry, filterOwner, sortId, sortDesc, page, pageSize, clampAfterLoad, getAuthHeader]);

    // #1000: a new search, filter or sort starts again at page 1 — in the same pass, so the
    // old page is never fetched first (one request per change, not two) — and clears the
    // selection (the Contacts rule, #863); moving between pages keeps it.
    const listKey = JSON.stringify([search, filterStatus, filterIndustry, filterOwner, sortId, sortDesc]);
    const loadedKey = useRef(null);
    useEffect(() => {
        const changed = loadedKey.current !== null && loadedKey.current !== listKey;
        loadedKey.current = listKey;
        if (changed && page !== 1) { resetPage(); return; }
        loadCompanies();
    }, [loadCompanies, listKey, page, resetPage]);
    useEffect(() => { setSelectedIds(new Set()); }, [search, filterStatus, filterIndustry, filterOwner, sortId, sortDesc, activeTab]);

    // Load team members for assignment
    useEffect(() => {
        const loadTeam = async () => {
            try {
                const res = await axios.get(`${API}/accounts/team-members`, { headers: getAuthHeader() });
                setTeamMembers(res.data || []);
            } catch { /* ignore */ }
        };
        loadTeam();
    }, [getAuthHeader]);

    const handleAssignMember = async (accountId, userId) => {
        try {
            await axios.put(`${API}/accounts/${accountId}/assign`, { add_member: userId }, { headers: getAuthHeader() });
            toast.success('Team member assigned');
            loadCompanies();
        } catch { toast.error('Failed to assign'); }
    };

    const handleRemoveMember = async (accountId, userId) => {
        try {
            await axios.put(`${API}/accounts/${accountId}/assign`, { remove_member: userId }, { headers: getAuthHeader() });
            toast.success('Team member removed');
            loadCompanies();
        } catch { toast.error('Failed to remove'); }
    };

    const handleSetOwner = async (accountId, userId) => {
        try {
            await axios.put(`${API}/accounts/${accountId}/assign`, { owner_id: userId }, { headers: getAuthHeader() });
            toast.success('Owner updated');
            loadCompanies();
        } catch { toast.error('Failed to update owner'); }
    };

    const handleSave = (saved) => {
        setCompanies(prev => {
            const idx = prev.findIndex(a => a.account_id === saved.account_id);
            if (idx >= 0) { const updated = [...prev]; updated[idx] = saved; return updated; }
            return [saved, ...prev];
        });
    };

    // Load one page of deleted companies (#1000)
    const { page: deletedPage, pageSize: deletedPageSize, clampAfterLoad: clampDeleted } = deletedPaging;
    const loadDeletedCompanies = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/accounts/deleted`, {
                params: { page: deletedPage, page_size: deletedPageSize }, headers: getAuthHeader(),
            });
            const rows = res.data.data || [];
            setDeletedCompanies(rows);
            setDeletedTotal(res.data.total || 0);
            clampDeleted(rows.length, res.data.total || 0);
        } catch { /* ignore */ }
    }, [deletedPage, deletedPageSize, clampDeleted, getAuthHeader]);

    useEffect(() => {
        if (activeTab === 'deleted') loadDeletedCompanies();
    }, [activeTab, loadDeletedCompanies]);

    const handleBulkDelete = async () => {
        if (deleteConfirmText !== 'DELETE') return;
        setDeleting(true);
        try {
            const ids = Array.from(selectedIds);
            await axios.delete(`${API}/accounts/bulk`, { data: { account_ids: ids }, headers: getAuthHeader() });
            toast.success(`${ids.length} ${ids.length > 1 ? 'companies' : 'company'} deleted`);
            setSelectedIds(new Set());
            setShowDeleteModal(false);
            setDeleteConfirmText('');
            loadCompanies();
        } catch { toast.error('Failed to delete companies'); }
        finally { setDeleting(false); }
    };

    const handleRestore = async (ids) => {
        const restoreIds = ids || Array.from(selectedIds);
        if (!restoreIds.length) return;
        setRestoring(true);
        try {
            await axios.put(`${API}/accounts/restore`, { account_ids: restoreIds }, { headers: getAuthHeader() });
            toast.success(`${restoreIds.length} ${restoreIds.length > 1 ? 'companies' : 'company'} restored`);
            setSelectedIds(new Set());
            loadDeletedCompanies();
            loadCompanies();
        } catch { toast.error('Failed to restore companies'); }
        finally { setRestoring(false); }
    };

    const handleBulkTag = async () => {
        if (!bulkTags.length) { toast.error('Please add at least one tag'); return; }
        setBulkTagging(true);
        try {
            const res = await axios.post(`${API}/accounts/bulk-tag`, {
                account_ids: Array.from(selectedIds),
                action: bulkTagAction,
                tags: bulkTags,
            }, { headers: getAuthHeader() });
            const { tone, message } = bulkTagMessage({ action: bulkTagAction, count: res.data.count, changed: res.data.changed, noun: 'companies' });
            toast[tone](message);
            setShowBulkTagModal(false);
            setBulkTags([]);
            setSelectedIds(new Set());
            loadCompanies();
        } catch { toast.error('Failed to update tags'); }
        finally { setBulkTagging(false); }
    };

    const isAdmin = user?.is_admin;
    const pageRows = activeTab === 'deleted' ? deletedCompanies : companies;
    const offPageSelected = [...selectedIds].filter(id => !pageRows.some(c => c.account_id === id)).length;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Companies</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <Building2 className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-sm text-stone-500" data-field="account.count">{activeTab === 'deleted' ? `${deletedTotal} deleted` : `${total} companies`}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {selectedIds.size > 0 && offPageSelected > 0 && (
                        <span className="text-xs text-stone-500 tabular-nums whitespace-nowrap" data-testid="selection-off-page">
                            {selectedIds.size} selected · {offPageSelected} on other pages
                        </span>
                    )}
                    {selectedIds.size > 0 && activeTab === 'companies' && (
                        <>
                        <button onClick={() => { setBulkTags([]); setBulkTagAction('add'); setShowBulkTagModal(true); }}
                            className="flex items-center gap-2 px-4 py-2 bg-stone-700 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors">
                            <Tag className="w-4 h-4" /> Tag ({selectedIds.size})
                        </button>
                        <button onClick={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors">
                            <Trash2 className="w-4 h-4" /> Delete ({selectedIds.size})
                        </button>
                        </>
                    )}
                    {selectedIds.size > 0 && activeTab === 'deleted' && (
                        <button onClick={() => handleRestore()} disabled={restoring}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                            <RotateCcw className="w-4 h-4" /> Restore ({selectedIds.size})
                        </button>
                    )}
                    {isAdmin && (
                        <button onClick={() => { setActiveTab(activeTab === 'deleted' ? 'companies' : 'deleted'); setSelectedIds(new Set()); }}
                            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                activeTab === 'deleted' ? 'bg-red-50 text-red-700 border border-red-200' : 'text-stone-500 hover:text-stone-700 hover:bg-stone-100'
                            }`}>
                            <Trash2 className="w-3.5 h-3.5" /> Deleted
                        </button>
                    )}
                    <button onClick={() => setShowModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors">
                        <Plus className="w-4 h-4" /> New Company
                    </button>
                </div>
            </div>

            {/* Toolbar: View Selector + Filters + Add Column */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-1 flex-wrap">
                    <ViewSelector
                        views={views}
                        activeViewId={activeViewId}
                        isDirty={isDirty}
                        onSwitchView={switchView}
                        onSaveView={saveView}
                        onSaveAsView={saveAsView}
                        onDeleteView={deleteView}
                        onPushToOrg={pushToOrg}
                        isAdmin={isAdmin}
                    />
                    <div className="w-px h-6 bg-stone-200" />
                    <div className="relative flex-1 min-w-[200px] max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input type="text" placeholder="Search companies..." value={searchInput} onChange={e => setSearchInput(e.target.value)}
                            className="w-full pl-9 pr-3 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none capitalize">
                        <option value="">All Status</option>
                        {STATUSES.map(s => <option key={s.value} value={s.value} className="capitalize">{s.label}</option>)}
                    </select>
                    <select value={filterIndustry} onChange={e => setFilterIndustry(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                        <option value="">All Industries</option>
                        {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
                    </select>
                    <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                        <option value="">All Owners</option>
                        {teamMembers.map(m => (
                            <option key={m.user_id} value={m.user_id}>{m.name}</option>
                        ))}
                    </select>
                </div>
                <ColumnPicker
                    allFields={allFields}
                    visibleKeys={visibleKeySet}
                    lockedKey="name"
                    onToggle={(key, on) => (on ? addColumn(key) : removeColumn(key))}
                />
            </div>

            {/* Data Table */}
            {activeTab === 'companies' ? (
                loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                    </div>
                ) : (<>
                    <DataTable
                        data={companies}
                        visibleColumns={visibleColumns}
                        sorting={serverSorting}
                        onSortingChange={setSorting}
                        manualSorting
                        header={(
                            <TableRangeBar page={page} pageSize={pageSize} total={total} noun="companies"
                                onPageSizeChange={paging.setPageSize} pageSizeOptions={PAGE_SIZE_OPTIONS} />
                        )}
                        footer={(
                            <TablePagination variant="split" alwaysShow page={page} totalPages={paging.totalPages(total)}
                                total={total} pageSize={pageSize} onPageChange={paging.setPage}
                                onPageSizeChange={paging.setPageSize} />
                        )}
                        columnSizing={columnSizing}
                        onColumnSizingChange={setColumnSizing}
                        onRowClick={(row) => navigate(`/companies/${row.account_id}`)}
                        selectable
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        idField="account_id"
                    />
                    {/* Admin: quick assign buttons */}
                    {isAdmin && companies.length > 0 && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-stone-400">
                            <UserPlus className="w-3.5 h-3.5" />
                            <span>Click a company row, then use the "Assign Team" button on the detail page, or:</span>
                            {companies.filter(c => !c.team_members?.length && !c.owner_name).slice(0, 3).map(c => (
                                <button key={c.account_id} onClick={(e) => { e.stopPropagation(); setAssignAccount(c); setShowAssign(true); }}
                                    className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
                                    Assign: {c.name}
                                </button>
                            ))}
                        </div>
                    )}
                </>)
            ) : (
                /* Deleted companies table */
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    <TableRangeBar page={deletedPage} pageSize={deletedPageSize} total={deletedTotal} noun="deleted companies"
                        onPageSizeChange={deletedPaging.setPageSize} pageSizeOptions={PAGE_SIZE_OPTIONS} />
                    <div className="overflow-auto">
                    <table className="w-full">
                        <thead className="sticky top-0 z-10 bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className="px-3 py-2.5 text-left w-10">
                                    <input type="checkbox" aria-label="Select all rows on this page"
                                        checked={deletedCompanies.length > 0 && deletedCompanies.every(c => selectedIds.has(c.account_id))}
                                        ref={el => { if (el) el.indeterminate = deletedCompanies.some(c => selectedIds.has(c.account_id)) && !deletedCompanies.every(c => selectedIds.has(c.account_id)); }}
                                        onChange={(e) => {
                                            // #1000: only this page's rows change; other pages' picks stay.
                                            const next = new Set(selectedIds);
                                            deletedCompanies.forEach(c => (e.target.checked ? next.add(c.account_id) : next.delete(c.account_id)));
                                            setSelectedIds(next);
                                        }}
                                        className="w-4 h-4 rounded border-stone-300 cursor-pointer" />
                                </th>
                                {['Company', 'Industry', 'Status', 'Deleted By', 'Deleted At', ''].map(h => (
                                    <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {deletedCompanies.length === 0 ? (
                                <tr><td colSpan={7} className="text-center py-16 text-sm text-stone-400">No deleted companies</td></tr>
                            ) : deletedCompanies.map(c => (
                                <tr key={c.account_id} className="border-b border-stone-100 hover:bg-stone-50">
                                    <td className="px-3 py-2.5">
                                        <input type="checkbox" checked={selectedIds.has(c.account_id)}
                                            onChange={(e) => {
                                                const next = new Set(selectedIds);
                                                if (e.target.checked) next.add(c.account_id);
                                                else next.delete(c.account_id);
                                                setSelectedIds(next);
                                            }}
                                            className="w-4 h-4 rounded border-stone-300 cursor-pointer" />
                                    </td>
                                    <td className="px-3 py-2.5 text-sm font-medium text-stone-900">{c.name}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-600">{c.industry || '—'}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-600 capitalize">{c.status || '—'}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-500">{c.deleted_by_name || '—'}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-500">{c.deleted_at ? new Date(c.deleted_at).toLocaleDateString() : '—'}</td>
                                    <td className="px-3 py-2.5">
                                        <button onClick={() => handleRestore([c.account_id])}
                                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                                            <RotateCcw className="w-3 h-3" /> Restore
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    <TablePagination variant="split" alwaysShow page={deletedPage} totalPages={deletedPaging.totalPages(deletedTotal)}
                        total={deletedTotal} pageSize={deletedPageSize} onPageChange={deletedPaging.setPage}
                        onPageSizeChange={deletedPaging.setPageSize} />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Delete {selectedIds.size} {selectedIds.size > 1 ? 'Companies' : 'Company'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <p className="text-sm text-stone-600">
                            This will soft-delete the selected {selectedIds.size > 1 ? 'companies' : 'company'}. They can be restored from the "Deleted" tab.
                        </p>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">
                                Type DELETE to confirm
                            </label>
                            <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
                                placeholder="DELETE" autoFocus
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400" />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                            <button onClick={handleBulkDelete} disabled={deleteConfirmText !== 'DELETE' || deleting}
                                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Bulk Tag Modal */}
            <Dialog open={showBulkTagModal} onOpenChange={setShowBulkTagModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-stone-900">
                            <Tag className="w-5 h-5" />
                            Tag {selectedIds.size} Compan{selectedIds.size > 1 ? 'ies' : 'y'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Action</label>
                            <select value={bulkTagAction} onChange={e => setBulkTagAction(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                <option value="add">Add Tags</option>
                                <option value="remove">Remove Tags</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Tags</label>
                            <TagInput
                                value={bulkTags}
                                onChange={setBulkTags}
                                placeholder={bulkTagAction === 'add' ? 'Add tags...' : 'Tags to remove...'}
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowBulkTagModal(false)}
                                className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                            <button onClick={handleBulkTag} disabled={!bulkTags.length || bulkTagging}
                                className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed">
                                {bulkTagging ? 'Updating...' : `${bulkTagAction === 'add' ? 'Add' : 'Remove'} Tags`}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Company Modal */}
            <CompanyModal
                open={showModal}
                onClose={() => setShowModal(false)}
                onSave={handleSave}
            />

            {/* Team Assignment Dialog */}
            <Dialog open={showAssign} onOpenChange={setShowAssign}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-stone-700" />
                            Assign Team — {assignAccount?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {assignAccount && (
                        <div className="space-y-4 py-2">
                            {/* Current Owner */}
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Owner</label>
                                <select
                                    value={assignAccount.owner_id || ''}
                                    onChange={e => { handleSetOwner(assignAccount.account_id, e.target.value); setAssignAccount(prev => ({ ...prev, owner_id: e.target.value })); }}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                >
                                    <option value="">Unassigned</option>
                                    {teamMembers.map(m => (
                                        <option key={m.user_id} value={m.user_id}>{m.name} ({m.role})</option>
                                    ))}
                                </select>
                            </div>

                            {/* Current Team */}
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                                    Team Members ({assignAccount.team_members?.length || 0})
                                </label>
                                <div className="space-y-1.5 mb-3">
                                    {(assignAccount.team_members || []).map(member => (
                                        <div key={member.user_id} className="flex items-center justify-between px-3 py-2 bg-stone-50 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-stone-200 flex items-center justify-center text-[10px] font-bold text-stone-600">
                                                    {member.name?.charAt(0)?.toUpperCase()}
                                                </div>
                                                <span className="text-sm text-stone-700">{member.name}</span>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    handleRemoveMember(assignAccount.account_id, member.user_id);
                                                    setAssignAccount(prev => ({
                                                        ...prev,
                                                        team_members: prev.team_members.filter(m => m.user_id !== member.user_id)
                                                    }));
                                                }}
                                                className="p-1 text-stone-400 hover:text-red-500 transition-colors"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                    {(!assignAccount.team_members || assignAccount.team_members.length === 0) && (
                                        <p className="text-xs text-stone-400 text-center py-2">No team members assigned</p>
                                    )}
                                </div>

                                {/* Add member */}
                                <div className="flex items-center gap-2">
                                    <select
                                        value={selectedMember}
                                        onChange={e => setSelectedMember(e.target.value)}
                                        className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                    >
                                        <option value="">Select team member...</option>
                                        {teamMembers
                                            .filter(m => !(assignAccount.team_members || []).some(tm => tm.user_id === m.user_id))
                                            .map(m => (
                                                <option key={m.user_id} value={m.user_id}>{m.name} ({m.role})</option>
                                            ))
                                        }
                                    </select>
                                    <Button size="sm" disabled={!selectedMember} onClick={() => {
                                        if (selectedMember) {
                                            handleAssignMember(assignAccount.account_id, selectedMember);
                                            const member = teamMembers.find(m => m.user_id === selectedMember);
                                            setAssignAccount(prev => ({
                                                ...prev,
                                                team_members: [...(prev.team_members || []), { user_id: selectedMember, name: member?.name || '' }]
                                            }));
                                            setSelectedMember('');
                                        }
                                    }}>
                                        <UserPlus className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
